// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { Button } from '@mui/material';
import type { AdvisoryProposalContractResponseBody } from '@shared/data-access/interfaces/api/brokerage/client-types';
import { AppIcon, Table } from '@shared/ui-kit';
import { Translation } from '@shared/ui-kit/providers';
import { formatDate } from '@shared/ui-kit/utils';
import { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router';
import styles from './silver-alpha-home.module.css';

export interface SilverAlphaStrategyCardProps {
  proposal: AdvisoryProposalContractResponseBody;
}

export const SilverAlphaStrategyCard: FC<SilverAlphaStrategyCardProps> = ({
  proposal,
}) => {
  const { t, i18n } = useTranslation();

  const perimeter = useMemo(
    () =>
      (proposal.institutions ?? [])
        .map(institution => institution.name?.trim())
        .filter(Boolean)
        .join(', '),
    [proposal.institutions],
  );

  const contractType = t(
    `activation-step2.advisory-types.${proposal.advisory_type}.label`,
    { ns: 'components' },
  );

  const statusLabelKey =
    proposal.status === 'active'
      ? 'common.active'
      : proposal.status === 'inactive'
      ? 'common.inactive'
      : 'common.pending';

  return (
    <article className={styles.card} data-testid="silver-alpha-strategy-card">
      <div className={styles.platinumHeader}>
        <p className={styles.cardEyebrow}>
          <Translation
            i18nKey="silver-alpha-home.strategy-card.label"
            ns="components"
          />
        </p>
        <span
          className={`${styles.statusPill}${
            proposal.status === 'active' ? ` ${styles.statusPillActive}` : ''
          }`}
          data-testid={`advisory-proposal-status-${proposal.id}`}
        >
          <span className={styles.statusDot} aria-hidden />
          {t(statusLabelKey, { ns: 'common' })}
        </span>
      </div>

      <h2 className={styles.cardTitle}>
        <Translation
          i18nKey="silver-alpha-home.strategy-card.title"
          ns="components"
        />
      </h2>
      {proposal.advisor?.name ? (
        <p className={styles.strategyAdvisor}>{proposal.advisor.name}</p>
      ) : null}

      <dl className={styles.strategyGrid}>
        <div className={styles.strategyField}>
          <dt className={styles.fieldLabel}>
            <Translation
              i18nKey="advisory-proposal.contract-type"
              ns="components"
            />
          </dt>
          <dd className={styles.fieldValue}>{contractType}</dd>
        </div>
        <div className={styles.strategyField}>
          <dt className={styles.fieldLabel}>
            {t('common.commission', { ns: 'common' })}
          </dt>
          <dd className={styles.fieldValue}>
            <span className="inline-flex flex-wrap items-baseline gap-1">
              <Table.Data
                value={proposal.commission_managed_assets}
                format="percentage"
                formatOptions={{
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }}
              />
              <span>
                {t('advisory-proposal.commission-suffix', { ns: 'components' })}
              </span>
            </span>
          </dd>
        </div>
        <div className={styles.strategyField}>
          <dt className={styles.fieldLabel}>
            <Translation
              i18nKey="silver-alpha-home.strategy-card.created"
              ns="components"
            />
          </dt>
          <dd className={styles.fieldValue}>
            {formatDate(proposal.created_at, 'standard', i18n.language)}
          </dd>
        </div>
        <div className={styles.strategyField}>
          <dt className={styles.fieldLabel}>
            <Translation
              i18nKey="silver-alpha-home.strategy-card.updated"
              ns="components"
            />
          </dt>
          <dd className={styles.fieldValue}>
            {formatDate(proposal.updated_at, 'standard', i18n.language)}
          </dd>
        </div>
        {perimeter ? (
          <div className={styles.strategyFieldFull}>
            <dt className={styles.fieldLabel}>
              <Translation i18nKey="advisory-proposal.perimeter" ns="components" />
            </dt>
            <dd className={styles.fieldValue}>{perimeter}</dd>
          </div>
        ) : null}
      </dl>

      <div className={styles.strategyFooter}>
        <p className={styles.issuer}>
          <Translation i18nKey="advisory-proposal.issuer" ns="components" />
        </p>
        <Button
          color="secondary"
          component={RouterLink}
          data-testid="silver-alpha-strategy-link"
          endIcon={<AppIcon icon="ArrowRight" size="sm" />}
          size="medium"
          sx={{ textTransform: 'none' }}
          to="/silver-alpha/documents"
          variant="outlined"
        >
          <Translation
            i18nKey="silver-alpha-home.strategy-card.view"
            ns="components"
          />
        </Button>
      </div>
    </article>
  );
};
